import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import type { SupabaseClient } from '../../lib/supabase';
import { trackerKeys } from './hooks';

/**
 * The "Next action" box on the Lead and Prospect drawers.
 *
 * Kyu types a title and, optionally, a due date; this writes one row into
 * `public.tasks` through `create_sales_followup` (20270149000100), tagged to
 * whichever record the drawer is open on. That RPC is the only writer —
 * `public.tasks` has no browser INSERT grant at all, by design (20260801000200:
 * "revoke all on public.tasks from anon, authenticated") — so every task this
 * app creates goes through a security-definer function that resolves the
 * archived Sales follow-ups holder project and stamps the caller as creator.
 *
 * This used to write straight into `public.sales_tasks`. That table lost its
 * insert/update/delete grant in 20270146000100 when its rows moved into
 * `public.tasks`, and this box was the one call site nobody repointed — every
 * save here failed against a table the browser can no longer write, which is
 * the bug Kyu reported ("That change could not be saved. Try again.").
 *
 * The read below is scoped to one record rather than the shared open-tasks
 * cache `useLeadFollowups` keeps for the whole Tracker list: the drawer only
 * ever needs one lead's or one prospect's next task, and the ordering
 * (dated first, earliest due wins; undated tasks last, oldest first) is
 * cheaper to ask Postgres for than to recompute over every open task in the
 * app on every drawer open.
 */

export type NextActionRecordKind = 'lead' | 'prospect';

export interface OpenFollowup {
  id: string;
  title: string;
  due_date: string | null;
  created_at: string;
}

const FOLLOWUP_COLUMNS = 'id, title, due_date, created_at';

function recordColumn(kind: NextActionRecordKind): 'lead_id' | 'prospect_id' {
  return kind === 'lead' ? 'lead_id' : 'prospect_id';
}

export const nextActionKeys = {
  for: (kind: NextActionRecordKind, recordId: string) =>
    [...trackerKeys.all, 'nextAction', kind, recordId] as const,
};

async function fetchNextActionFor(
  client: SupabaseClient,
  kind: NextActionRecordKind,
  recordId: string,
): Promise<OpenFollowup | null> {
  const { data, error } = await client
    .from('tasks')
    .select(FOLLOWUP_COLUMNS)
    .eq(recordColumn(kind), recordId)
    .eq('is_completed', false)
    // Dated tasks first, earliest due wins; undated tasks sort after every
    // dated one, oldest-created first among themselves. The first row is
    // therefore already "the one to show" — no client-side picking needed.
    .order('due_date', { ascending: true, nullsFirst: false })
    .order('created_at', { ascending: true })
    .limit(1);
  if (error) {
    throw new Error(error.message);
  }
  return data === null ? null : (data.at(0) ?? null);
}

export function useNextActionFor(kind: NextActionRecordKind, recordId: string) {
  const { client } = useAuth();
  return useQuery({
    queryKey: nextActionKeys.for(kind, recordId),
    queryFn: () => fetchNextActionFor(client, kind, recordId),
    retry: false,
  });
}

export interface CreateNextActionInput {
  title: string;
  due: string | null;
  kind: NextActionRecordKind;
  recordId: string;
}

export function useCreateNextAction() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateNextActionInput): Promise<string> => {
      const { data, error } = await client.rpc('create_sales_followup', {
        p_title: input.title,
        ...(input.due === null ? {} : { p_due: input.due }),
        ...(input.kind === 'lead' ? { p_lead: input.recordId } : { p_prospect: input.recordId }),
      });
      if (error) {
        throw new Error(error.message);
      }
      return typeof data === 'string' ? data : '';
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: nextActionKeys.for(variables.kind, variables.recordId),
      });
      // The same row can show on the Sales Tasks tab, My Tasks and the
      // Tracker's own follow-up list — all stale the moment this lands.
      void queryClient.invalidateQueries({ queryKey: ['sales-tasks'] });
      void queryClient.invalidateQueries({ queryKey: ['my-tasks'] });
      void queryClient.invalidateQueries({ queryKey: trackerKeys.all });
    },
  });
}

/** Errors `create_sales_followup` raises, in words somebody can act on. */
export function nextActionErrorMessage(raw: string): string {
  if (raw.includes('not_a_sales_member')) {
    return 'You need sales access to set a task.';
  }
  if (raw.includes('sales_followup_title_required')) {
    return 'A task needs a title.';
  }
  if (raw.includes('lead_not_found') || raw.includes('prospect_not_found')) {
    return 'That record no longer exists.';
  }
  if (raw.includes('no_followup_home')) {
    return 'Tasks need a TAP admin account to exist. Ask an admin to sign in once.';
  }
  return 'That change could not be saved. Try again.';
}

/**
 * How the due date reads on the badge. Matches the wording the old
 * `sales_tasks`-backed box used, so this reads the same as before to anybody
 * who has seen it — only the plumbing underneath changed.
 */
export function dueBadgeLabel(dueDate: string | null, todayIso: string): string {
  if (dueDate === null) {
    return 'No due date';
  }
  const days = Math.round(
    (Date.parse(`${dueDate}T00:00:00Z`) - Date.parse(`${todayIso}T00:00:00Z`)) / 86_400_000,
  );
  if (days < 0) {
    return days === -1 ? 'Overdue by 1 day' : `Overdue by ${String(-days)} days`;
  }
  if (days === 0) {
    return 'Due today';
  }
  return `Due ${dueDate}`;
}

export function isFollowupOverdue(dueDate: string | null, todayIso: string): boolean {
  return dueDate !== null && dueDate < todayIso;
}
